// useLogStore.ts

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { useEffect } from 'react';

type StartLogData = {
  ticketId: string | number;
  logCategoryId: string | number;
  subLogCategoryId?: string | number | null;
  internalTicketTypeId?: string | number | null;
  internalSubTicketTypeId?: string | number | null;
  internalSubTicketTypeCount?: number | null;
  comment: string | null;
  azureTaskId?: string | null;
}

type StopLogData = {
  comment?: string | null;
}
interface LogState {
  isActive: boolean;
  isPaused: boolean;
  startTime: number | null;
  duration: number;
  ticket: any;
  log: any;
  isSystemPaused?: boolean;
  startLog: (data: StartLogData) => Promise<{ success: boolean; errors?: string[], message?: string }>;
  resumeLog: (isSystemResume?: boolean) => Promise<{ success: boolean; errors?: string[] }>;
  pauseLog: (isUserPaused?: boolean) => void;
  stopLog: (data: StopLogData) => Promise<{ success: boolean; errors?: string[], message?: string }>;
}

const secretKey = 'your-secret-key';

const getCsrfToken = () => {
  const tokenMeta = document.querySelector('meta[name="csrf-token"]');
  return tokenMeta ? tokenMeta.getAttribute('content') || '' : '';
};

const useLogStore = create<LogState>()(
  persist(
    (set, get) => ({
      isActive: false,
      isPaused: false,
      startTime: null,
      duration: 0,
      ticket: null,
      log: null,
      startLog: async (data: StartLogData) => {
        if (!get().isActive) {
          const csrfToken = getCsrfToken();
          try {
            const response = await axios.post('/logs', {
              log: {
                ticket_id: data.ticketId ?? null,
                log_category_id: data.logCategoryId,
                sub_log_category_id: data.subLogCategoryId,
                internal_ticket_type_id: data.internalTicketTypeId,
                internal_sub_ticket_type_id: data.internalSubTicketTypeId,
                internal_sub_ticket_type_count: data.internalSubTicketTypeCount,
                comment: data.comment,
                azure_task_id: data.azureTaskId,
              },
            }, {
              headers: {
                'X-CSRF-Token': csrfToken,
              },
            });

            if (response.data.success) {
              set({
                isActive: true,
                isPaused: false,
                startTime: Date.now(),
                duration: 0,
                ticket: response.data.ticket,
                log: response.data.log,
              });
              return { success: true, message: "Log started successfully." };
            } else {
              return { success: false, errors: response.data.errors || ['Failed to start log'], message: response.data.errors.join(', ') };
            }
          } catch (error: any) {
            if ((error as any).response) {
              return { success: false, errors: (error as any).response.data.errors, message: (error as any).response.data.errors.join(', ') };
            } else {
              return { success: false, errors: ['An unexpected error occurred. Please try again.'], message: 'An unexpected error occurred. Please try again.' };
            }
          }
        }
        return { success: true };
      },
      resumeLog: async (isSystemResume) => {
        if (isSystemResume) {
          if (get().isPaused && get().isSystemPaused) {
            set({
              isPaused: false,
              startTime: Date.now(),
              isSystemPaused: false,
            });
          }
        } else {
          if (get().isPaused) {
            set({
              isPaused: false,
              startTime: Date.now(),
            });
          }
        }
        
        return { success: true };
      },
      pauseLog: (isSystemPaused) => {
        if (get().isActive && !get().isPaused) {
          const now = Date.now();
          const elapsed = Math.floor((now - (get().startTime ?? now)) / 1000);
          set({
            isPaused: true,
            duration: get().duration + elapsed,
            startTime: null,
            isSystemPaused,
          });
        }
      },
      stopLog: async (data: StopLogData) => {
        if (get().isActive) {
          const now = Date.now();
          const elapsed = get().isPaused
            ? 0
            : Math.floor((now - (get().startTime ?? now)) / 1000);
          const totalDuration = get().duration + elapsed;

          const csrfToken = getCsrfToken();
          try {
            const response = await axios.put(`/logs/${get().log.id}`, {
              log: {
                comment: data.comment,
                log_duration: totalDuration,
              },
            }, {
              headers: {
                'X-CSRF-Token': csrfToken,
              },
            });

            if (response.data.success) {
              set({
                isActive: false,
                isPaused: false,
                startTime: null,
                duration: 0,
                log: null,
                ticket: null,
              });
              return { success: true, message: response.data.message };
            } else {
              return { success: false, errors: response.data.errors || ['Failed to stop log'] };
            }
          } catch (error: any) {
            if ((error as any).response) {
              return { success: false, errors: (error as any).response.data.errors };
            } else {
              return { success: false, errors: ['An unexpected error occurred. Please try again.'] };
            }
          }
        }
        return { success: true };
      },
    }),
    {
      name: 'log-storage', // Key for localStorage
      storage: {
        getItem: (name) => {
          const encryptedState = localStorage.getItem(name);
          if (encryptedState) {
            try {
              const bytes = CryptoJS.AES.decrypt(encryptedState, secretKey);
              const decryptedState = bytes.toString(CryptoJS.enc.Utf8);
              return JSON.parse(decryptedState);
            } catch (e) {
              console.error('Failed to decrypt state:', e);
              return null;
            }
          }
          return null;
        },
        setItem: (name, value) => {
          try {
            const stringifiedValue = typeof value === 'string' ? value : JSON.stringify(value);
            const encryptedState = CryptoJS.AES.encrypt(stringifiedValue, secretKey).toString();
            localStorage.setItem(name, encryptedState);
          } catch (e) {
            console.error('Failed to encrypt and store state:', e);
          }
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        },
      },
    }
  )
);


const useLogStoreWithSync = () => {
  const store = useLogStore();

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "log-storage" && event.newValue) {
        try {
          const bytes = CryptoJS.AES.decrypt(event.newValue, secretKey);
          const decryptedState = bytes.toString(CryptoJS.enc.Utf8);

          const newState = JSON.parse(decryptedState);

          const currentState = useLogStore.getState();
          if (JSON.stringify(currentState) !== JSON.stringify(newState.state)) {
            useLogStore.setState(newState.state);
          }
        } catch (error) {
          console.error("Error parsing decrypted localStorage state:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [store]);

  return store;
};

export default useLogStoreWithSync;
